/**
 * CTO Directive #126.2 §P0-C — Voice acknowledgement intent helper.
 *
 * Client-side mirror of the backend classifier used by
 * ``POST /api/me/voice/classify-utterance``. Reproducing the small
 * keyword table on the client lets the Living Presence page react
 * to "duyuyorum / duymuyorum / tekrar et / dur / ses ayarları"
 * INSIDE the mic stream without a network round-trip. The backend
 * remains the canonical source of truth (tested in
 * ``test_voice_intent_classifier.py``).
 *
 * Behaviour is deliberately conservative:
 *   * A short phrase that only matches one intent → return it.
 *   * Anything ambiguous → return AUDIO_INTENT_UNKNOWN so the caller
 *     forwards the utterance to the normal LLM pipeline instead of
 *     inventing an action.
 */

export const AUDIO_INTENT = Object.freeze({
  ACKNOWLEDGED: "audio_acknowledged",
  NOT_HEARD: "audio_not_heard",
  REPEAT_REQUESTED: "audio_repeat_requested",
  STOP_REQUESTED: "audio_stop_requested",
  OUTPUT_HELP_REQUESTED: "audio_output_help_requested",
  GREETING: "greeting",
  UNKNOWN: "unknown",
});

const TR_FOLD = {
  ı: "i", İ: "i", ş: "s", Ş: "s", ğ: "g", Ğ: "g",
  ü: "u", Ü: "u", ö: "o", Ö: "o", ç: "c", Ç: "c",
};

function fold(text) {
  if (!text) return "";
  const lowered = String(text).toLowerCase().trim();
  let out = "";
  for (const ch of lowered) out += TR_FOLD[ch] || ch;
  return out.replace(/\s+/g, " ");
}

// Ordered — stop has priority for safety; help before repeat; repeat
// before not-heard; not-heard before ack.
const PATTERNS = [
  // CTO #129 §P0.2 — living greeting (exact short greetings only).
  { intent: AUDIO_INTENT.GREETING,
    re: /^(merhaba|selam|gunaydin|iyi aksamlar|iyi gunler|hello|hi|hey|good (morning|evening|afternoon))( mi| me)?[.!]?$/ },
  { intent: AUDIO_INTENT.STOP_REQUESTED,
    re: /\b(sus|dur|bekle|kes(iver)?|iptal|vazgec|stop|hold on|be quiet|silent|silence|cancel|pause|shut up|nevermind|never mind)\b/ },
  { intent: AUDIO_INTENT.OUTPUT_HELP_REQUESTED,
    re: /(ses ayar|ses cikis|hoparlor|neden ses yok|ses gelmiyor mu diye bak|ses aygit|ses kaynagi|windows(ta|ta)? sesi|audio (settings?|output)|switch (the )?speaker|why (is there )?no sound|change (the )?output|speaker (device|selection)|check (my )?microphone)/ },
  { intent: AUDIO_INTENT.REPEAT_REQUESTED,
    re: /\b(tekrar( et| soyle| konus)?|bir daha soyle|bir daha konus|soylediginizi tekrarlar misin|tekrarlar misin|tekrarlayabilir misin|tekrar dinle(mek)? istiyorum|say (that|it) again|repeat( that| it| please)?|one more time|could you repeat|what was that)\b/ },
  { intent: AUDIO_INTENT.NOT_HEARD,
    re: /(sesin gelmedi|sesin gelmiyor|sesini duyamadim|seni duyamadim|duyamadim|sesi duymuyorum|sesi duyamiyorum|ses(i)? cikmadi|ses cikm[ıi]yor|\bduymuyorum\b|can'?t hear (you|it)|i can'?t hear|no sound came|i (didn'?t|do not) hear (anything|you)|silence)/ },
  { intent: AUDIO_INTENT.ACKNOWLEDGED,
    re: /(sesin geliyor|sesini duyuyorum|seni duyuyorum|\bduyuyorum\b|tamam(?:, devam et)?|devam et|anladim(?:, devam et)?|net duyuluyor|gayet net|\bi hear (you|it)\b|\bi can hear (you|it)\b|got it, go on|go ahead|please continue|loud and clear|understood, continue)/ },
];

export function classifyAudioIntent(text) {
  const folded = fold(text);
  if (!folded) return AUDIO_INTENT.UNKNOWN;
  for (const p of PATTERNS) {
    if (p.re.test(folded)) return p.intent;
  }
  return AUDIO_INTENT.UNKNOWN;
}

// CTO #126.3 §P0-B — mirror of the backend command-likeness stems.
// A short, audio-command-shaped utterance with no full match means
// ME should ask "Bunu nasıl yapmamı istersin?" (spoken once) rather
// than forwarding to the LLM. Backend remains canonical.
const COMMAND_STEMS = [
  "ses", "duy", "hoparlor", "mikrofon", "tekrar", "dinle",
  "audio", "sound", "hear", "speaker", "volume", "repeat", "mute",
];

export function isCommandLike(text) {
  const folded = fold(text);
  if (!folded || folded.split(" ").length > 6) return false;
  return COMMAND_STEMS.some((s) => folded.includes(s));
}

// CTO #127 §P0-E — natural cast commands. Deterministic client-side
// gate; the backend /me/cast/intent parser is canonical.
const CAST_RE = /(yayinla|yayin yap|yayini (durdur|kes|bitir|kapat)|ekrani unut|hangi ekrana yayin|nereye yayin|(ekrana|televizyona|tv'?ye) (ac|gonder|tasi|yansit)|ekranlari tara|ekran tara|cihazlari tara|guvenilir yap)/;

export function isCastCommand(text) {
  const folded = fold(text);
  if (!folded) return false;
  return CAST_RE.test(folded);
}

// CTO #129.1 — spoken pilot health summary commands.
const PILOT_STATUS_RE = /(pilot durumu|son 24 saatte sorun|sistem saglikli|pilot ozeti|(ses|cihaz|yayin|mikrofon) .*(hata|sorun).*var mi)/;

const ACCEPTANCE_REPORT_RE = /(fiziksel )?kabul raporu(nu)? (indir|hazirla|ver)/;

export function isAcceptanceReportCommand(text) {
  if (!text) return false;
  const folded = fold(text);
  return ACCEPTANCE_REPORT_RE.test(folded);
}

// "YouTube'da Mozart aç" / "youtube aç" / "youtube'da haberleri oynat"
export function parseYoutubeCommand(text) {
  if (!text) return null;
  const folded = fold(text);
  if (!/(you ?tube|yutup|yutub)/.test(folded)) return null;
  if (!/(ac|acar|oynat|izle|dinle|baslat|goster)/.test(folded)) return null;
  const q = folded
    .replace(/(you ?tube|yutup|yutub)('?[dt][ae])?/g, " ")
    .replace(/\b(acar\s*misin|ac|oynat|izlet|izle|dinlet|dinle|baslat|goster|lutfen|bana|bir|sey)\b/g, " ")
    .replace(/[^a-z0-9 ]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  return { query: q };
}

export function isPilotStatusCommand(text) {
  const folded = fold(text);
  if (!folded) return false;
  return PILOT_STATUS_RE.test(folded);
}
